import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type AdminCycle, type ScoredApplicant } from '../../api.ts';
import {
  ErrorNotice, Loading, formatDate, Select, rowLinkProps,
} from '../../components/Bits.tsx';
import { AdminChrome, useAdminSession } from './AdminChrome.tsx';
import { useSorting, applySorting } from '../../components/SortableTable.tsx';

type SortKey =
  | 'name' | 'city' | 'income' | 'siblings' | 'gpa' | 'test' | 'total' | 'submitted';

/** A score that could not be computed shows as a dash, never as a zero. */
function Points({ value }: { value: number | null | undefined }) {
  if (value === null || value === undefined) {
    return <span className="needs-review" title="Could not be scored — please review">—</span>;
  }
  return <>{value}</>;
}

export function AdminHome() {
  const navigate = useNavigate();
  const { data: session, isPending: sessionPending } = useAdminSession();
  const [year, setYear] = useState<number | null>(null);
  const [showIncomplete, setShowIncomplete] = useState(false);
  // Starts on the strongest applications, which is how the board reads the list; clicking
  // through a column three times returns to the server's order, alphabetical by name.
  const sorting = useSorting<SortKey>({ key: 'total', direction: 'desc' });

  const cycles = useQuery({
    queryKey: ['admin', 'cycles'],
    queryFn: () => api.get<{ cycles: AdminCycle[] }>('/api/admin/cycles'),
    enabled: Boolean(session?.admin),
  });

  const selectedYear = year ?? cycles.data?.cycles.find((c) => c.isCurrent)?.year ?? null;

  const applicants = useQuery({
    queryKey: ['admin', 'applicants', selectedYear],
    queryFn: () =>
      api.get<{ year: number; applicants: ScoredApplicant[] }>(
        `/api/admin/applicants?year=${selectedYear}`),
    enabled: Boolean(session?.admin && selectedYear),
  });

  const incomplete = useQuery({
    queryKey: ['admin', 'incomplete', selectedYear],
    queryFn: () =>
      api.get<{ applicants: Array<{ id: number; first_name: string | null; last_name: string | null; email: string; updated_at: string }> }>(
        `/api/admin/incomplete?year=${selectedYear}`),
    enabled: Boolean(session?.admin && selectedYear && showIncomplete),
  });

  if (sessionPending) return <main id="main"><Loading /></main>;
  if (!session?.admin) return <Navigate to="/admin/signin" replace />;

  const rows = applySorting(applicants.data?.applicants ?? [], sorting.sort, {
    name: (a) => [a.lastName, a.firstName].filter(Boolean).join(', ') || a.email,
    city: (a) => [a.city, a.state].filter(Boolean).join(', '),
    income: (a) => a.incomePoints,
    siblings: (a) => a.siblingPoints,
    gpa: (a) => a.gpaPoints,
    test: (a) => a.testPoints,
    total: (a) => a.totalPoints,
    submitted: (a) => a.submittedAt,
  });

  return (
    <>
      <AdminChrome admin={session.admin} />
      <main id="main">
        <h2>Applications</h2>

        <div className="actions" style={{ marginTop: 0, marginBottom: '1rem' }}>
          <label style={{ margin: 0 }}>
            <span>Scholarship year</span>
            <Select
              value={selectedYear ?? ''}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {cycles.data?.cycles.map((c) => (
                <option key={c.id} value={c.year}>
                  {c.year} — {c.completed} submitted of {c.total}
                  {c.isCurrent ? ' (current)' : ''}
                </option>
              ))}
            </Select>
          </label>

          <span className="spacer" style={{ flex: 1 }} />

          {selectedYear && (
            <>
              <a
                className="tag"
                href={`/api/admin/applicants.csv?year=${selectedYear}`}
                style={{ padding: '0.5rem 1rem', textDecoration: 'none' }}
              >
                Scoring CSV
              </a>
              {/* Every application as one archive, for reading offline. Generated on demand
                  and streamed; nothing is written to disk. */}
              <a
                className="tag"
                href={`/api/admin/applicants.zip?year=${selectedYear}`}
                style={{ padding: '0.5rem 1rem', textDecoration: 'none' }}
                title="All submitted applications as PDFs, in one zip"
              >
                All PDFs
              </a>
            </>
          )}
          <button
            className="secondary"
            onClick={() => setShowIncomplete((v) => !v)}
          >
            {showIncomplete ? 'Hide' : 'Show'} unfinished
          </button>
        </div>

        <ErrorNotice error={cycles.error ?? applicants.error} />

        {applicants.isPending && selectedYear && <Loading what="Loading applications" />}

        {showIncomplete && (
          <div className="panel">
            <h3 style={{ marginTop: 0 }}>Started but not submitted</h3>
            {incomplete.isPending ? <Loading /> : (
              <div className="scroll-x">
                <table>
                  <thead>
                    <tr><th>Name</th><th>Email</th><th>Last touched</th></tr>
                  </thead>
                  <tbody>
                    {(incomplete.data?.applicants ?? []).map((a) => (
                      <tr key={a.id} {...rowLinkProps(navigate, `/admin/applicants/${a.id}`)}>
                        <td>
                          {/* Unfinished applications are worth opening too -- what a reviewer
                              wants to know is usually how far someone actually got. */}
                          <Link to={`/admin/applicants/${a.id}`}>
                            {[a.first_name, a.last_name].filter(Boolean).join(' ') || a.email}
                          </Link>
                        </td>
                        <td>{a.email}</td>
                        <td>{formatDate(a.updated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!incomplete.data?.applicants.length && (
                  <p className="empty">None.</p>
                )}
              </div>
            )}
          </div>
        )}

        {rows.length === 0 && !applicants.isPending && (
          <p className="empty">No submitted applications for {selectedYear}.</p>
        )}

        {rows.length > 0 && (
          <div className="panel scroll-x">
            <table>
              <thead>
                <tr>
                  <th {...sorting.headerProps('name')}>Name {sorting.indicator('name')}</th>
                  <th {...sorting.headerProps('city')}>City {sorting.indicator('city')}</th>
                  <th {...sorting.headerProps('income')} className={`num ${sorting.headerProps('income').className}`}>
                    Income {sorting.indicator('income')}
                  </th>
                  <th {...sorting.headerProps('siblings')} className={`num ${sorting.headerProps('siblings').className}`}>
                    Siblings {sorting.indicator('siblings')}
                  </th>
                  <th {...sorting.headerProps('gpa')} className={`num ${sorting.headerProps('gpa').className}`}>
                    GPA {sorting.indicator('gpa')}
                  </th>
                  <th {...sorting.headerProps('test')} className={`num ${sorting.headerProps('test').className}`}>
                    Test {sorting.indicator('test')}
                  </th>
                  <th {...sorting.headerProps('total')} className={`num ${sorting.headerProps('total').className}`}>
                    Objective total {sorting.indicator('total')}
                  </th>
                  <th {...sorting.headerProps('submitted')}>
                    Submitted {sorting.indicator('submitted')}
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} {...rowLinkProps(navigate, `/admin/applicants/${a.id}`)}>
                    <td>
                      <Link to={`/admin/applicants/${a.id}`}>
                        {[a.lastName, a.firstName].filter(Boolean).join(', ') || a.email}
                      </Link>
                    </td>
                    <td>{[a.city, a.state].filter(Boolean).join(', ')}</td>
                    <td className="num"><Points value={a.incomePoints} /></td>
                    <td className="num"><Points value={a.siblingPoints} /></td>
                    <td className="num"><Points value={a.gpaPoints} /></td>
                    <td className="num"><Points value={a.testPoints} /></td>
                    <td className="num"><strong>{a.totalPoints}</strong></td>
                    <td>{formatDate(a.submittedAt)}</td>
                    <td>
                      <a href={`/api/admin/applicants/${a.id}/pdf`}>PDF</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="hint">
          A dash means the value could not be scored — an unreadable GPA, or a test score with
          no known scale — and needs a person to look at it. It is not a score of zero.
          The objective total covers income, siblings, GPA and test results only; essays,
          service and references are judged by the board.
        </p>
      </main>
    </>
  );
}
